import { useEffect, useState } from 'react'
import { Activity, Container, Database, HardDrive, ShieldCheck, Wifi, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import api from '@/lib/api'

const POLL_MS = 15000

export default function SystemHealthPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = async () => {
    setRefreshing(true)
    try {
      const r = await api.get('/admin/system-health')
      setData(r.data); setError(null)
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    load()
    const iv = setInterval(load, POLL_MS)
    return () => clearInterval(iv)
  }, [isAdmin])

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Activity}
        eyebrow="Beheer"
        title="System Health"
        subtitle="Live status van docker, postgres, disks, certs & services"
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && <Card className="border-red-500/40"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}

      <div className="grid gap-4 lg:grid-cols-2">
        <DockerCard docker={data?.docker} />
        <PostgresCard postgres={data?.postgres} />
        <DisksCard disks={data?.disks} />
        <CertsCard certs={data?.certs} />
        <ServicesCard services={data?.services} />
        <MigrationsCard migrations={data?.migrations} />
      </div>

      {data?.ts && (
        <div className="text-xs text-muted-foreground text-right">
          Laatst gerefresht: {new Date(data.ts).toLocaleTimeString('nl-BE')}
        </div>
      )}
    </div>
  )
}

function StatusIcon({ healthy }) {
  if (healthy === true) return <CheckCircle2 className="h-4 w-4 text-emerald-400" />
  if (healthy === false) return <XCircle className="h-4 w-4 text-red-500" />
  return <AlertTriangle className="h-4 w-4 text-yellow-500" />
}

function DockerCard({ docker }) {
  const list = Array.isArray(docker) ? docker : []
  const upCount = list.filter(c => c.healthy).length
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Container className="h-4 w-4" /> Docker containers
          <Badge variant="outline" className="ml-auto">{upCount}/{list.length} up</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {docker?.error && <p className="text-xs text-red-500">⚠ {docker.error}</p>}
        {list.length === 0 && !docker?.error && <p className="text-xs text-muted-foreground">Geen containers</p>}
        {list.map(c => (
          <div key={c.name} className="flex items-center gap-2 text-sm py-0.5">
            <StatusIcon healthy={c.healthy} />
            <span className="font-mono truncate flex-1">{c.name}</span>
            <span className="text-xs text-muted-foreground truncate max-w-[45%]">{c.status}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function PostgresCard({ postgres }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Database className="h-4 w-4" /> Postgres
          <Badge variant="outline" className="ml-auto">{postgres?.status || 'unknown'}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        {postgres?.error && <p className="text-xs text-red-500">⚠ {postgres.error}</p>}
        {postgres?.status === 'online' && (
          <>
            <Row label="Actieve connecties" value={postgres.active_connections} />
            <Row label="DB size"             value={postgres.db_size} />
            <Row label="Uptime"              value={formatUptime(postgres.uptime_seconds)} />
          </>
        )}
      </CardContent>
    </Card>
  )
}

function DisksCard({ disks }) {
  const list = Array.isArray(disks) ? disks : []
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <HardDrive className="h-4 w-4" /> Disks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.length === 0 && <p className="text-xs text-muted-foreground">Geen disks</p>}
        {list.map((d, i) => (
          <div key={i} className="text-sm">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono text-xs">{d.mount}</span>
              <span className="ml-auto text-xs text-muted-foreground">{d.used_gb} / {d.total_gb} GB</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className={`h-full transition-all ${d.used_pct > 90 ? 'bg-red-500' : d.used_pct > 75 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                   style={{ width: `${d.used_pct}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function CertsCard({ certs }) {
  const list = Array.isArray(certs) ? certs : []
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> TLS certificates
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        {list.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Geen certificaten in <code>/app/ssl/</code> of <code>./ssl/</code>. Als TLS via nginx is
            geconfigureerd, mount die dir in de backend-container.
          </p>
        )}
        {list.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <StatusIcon healthy={c.healthy} />
            <span className="font-mono text-xs truncate flex-1">{c.path.split('/').pop()}</span>
            <span className={`text-xs ${c.days_until < 14 ? 'text-red-500' : c.days_until < 30 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
              nog {c.days_until}d
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function ServicesCard({ services }) {
  const list = Array.isArray(services) ? services : []
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Wifi className="h-4 w-4" /> Services
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-1.5 grid-cols-1 md:grid-cols-2 text-sm">
        {list.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <StatusIcon healthy={s.reachable} />
            <span className="truncate flex-1">{s.name}</span>
            {s.latency_ms != null && <span className="text-xs text-muted-foreground">{s.latency_ms}ms</span>}
            {s.status && <Badge variant="outline" className="text-xs">{s.status}</Badge>}
            {s.error && <span className="text-xs text-red-500">{s.error}</span>}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  )
}

function formatUptime(seconds) {
  if (!seconds) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return d > 0 ? `${d}d ${h}u` : h > 0 ? `${h}u ${m}m` : `${m}m`
}

/* Toont recentelijk gerunde migraties + faal-status. Als er een faal is,
   rood-getint bordje bovenaan zodat admin het niet mist. */
function MigrationsCard({ migrations }) {
  const m = migrations || {}
  const list = Array.isArray(m.recent) ? m.recent : []
  const hasFailure = !!m.has_failure
  return (
    <Card className={hasFailure ? 'border-red-500/60' : ''}>
      <CardContent className="p-4 space-y-2 text-sm">
        <div className="flex items-center gap-2 mb-2">
          <StatusIcon healthy={!hasFailure && list.length > 0} />
          <span className="font-semibold">Schema-migraties</span>
          <Badge variant="outline" className="ml-auto">
            {list.length} recent{hasFailure ? ` · ${m.failed_count} FAAL` : ''}
          </Badge>
        </div>
        {m?.error && <p className="text-xs text-red-500">⚠ {m.error}</p>}
        {list.length === 0 && !m?.error && <p className="text-xs text-muted-foreground">Geen data</p>}
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {list.map(row => (
            <div key={row.filename} className={`flex items-start gap-2 text-xs py-1 border-b border-border/30 last:border-0 ${!row.success ? 'text-red-400' : ''}`}>
              <StatusIcon healthy={row.success} />
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate">{row.filename}</div>
                {row.error_message && (
                  <div className="text-[10px] text-red-400 font-mono truncate" title={row.error_message}>
                    {row.error_message}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                {row.applied_at ? new Date(row.applied_at).toLocaleDateString('nl-BE') : ''}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
